import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAllBreathPages,
  getPnoeMeasurement,
  getPnoeMeasurementPhases,
} from "../../../../common/network/Api";
import {
  PnoeMeasurementDetail,
  PnoeMeasurementOperation,
  PnoePhase,
} from "../../entities/pnoeMeasurement";
import { isMeasurementLive } from "../../utils/pnoeMeasurementStatus";
import { describeBreathError } from "./usePnoeMeasurements";

// How often a recording measurement is re-read. The old page had a manual
// Refresh button; the requirements replace it with this.
const POLL_INTERVAL_MS = 15000;

/**
 * The measurement detail plus its phases, kept fresh while anything is moving.
 *
 * Polling runs only while the test is `in_progress`, and pauses whenever the
 * browser tab is hidden — so an idle detail page for a test that is no longer
 * recording makes exactly two requests and then stops.
 */
export const usePnoeMeasurementDetail = (measurementId: string) => {
  const [detail, setDetail] = useState<PnoeMeasurementDetail | null>(null);
  const [phases, setPhases] = useState<PnoePhase[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        setError(null);
        const [detailResponse, phasesResponse] = await Promise.all([
          getPnoeMeasurement(measurementId),
          fetchAllBreathPages<PnoePhase>((params) =>
            getPnoeMeasurementPhases(measurementId, params)
          ).catch((e) => {
            // Phases are supporting data — a failure there shouldn't blank the
            // whole page, which is why this is caught separately.
            console.warn("Error loading measurement phases:", e);
            return { data: [] as PnoePhase[], truncated: false };
          }),
        ]);
        setDetail(detailResponse ?? null);
        setPhases(
          [...phasesResponse.data].sort(
            (a, b) => Number(a.elapsed_time ?? 0) - Number(b.elapsed_time ?? 0)
          )
        );
      } catch (e) {
        console.warn("Error loading PNOE measurement:", e);
        setError(describeBreathError(e));
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [measurementId]
  );

  useEffect(() => {
    load();
  }, [load]);

  const live = isMeasurementLive(detail?.measurement.status);

  // `load` in a ref so changing it (or `live` flipping) doesn't tear down and
  // restart the interval mid-cycle.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!live) return;
    const tick = () => {
      if (document.visibilityState === "hidden") return;
      loadRef.current(true);
    };
    const handle = setInterval(tick, POLL_INTERVAL_MS);
    // Coming back to the tab shouldn't mean waiting out the rest of the interval.
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(handle);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [live]);

  /**
   * Apply a `MeasurementOperationPublic` (what complete/invalidate and every
   * PATCH return) to the in-memory detail, so the badges move the moment the
   * request resolves instead of on the next poll.
   */
  const applyOperation = useCallback((operation: PnoeMeasurementOperation) => {
    setDetail((current) =>
      current
        ? {
            ...current,
            measurement: {
              ...current.measurement,
              status: operation.status ?? current.measurement.status,
              report_status:
                operation.report_status ?? current.measurement.report_status,
              report_revision:
                operation.report_revision ?? current.measurement.report_revision,
            },
          }
        : current
    );
  }, []);

  return {
    detail,
    phases,
    loading,
    error,
    live,
    reload: load,
    applyOperation,
  };
};
